import { useTranslation } from "react-i18next";
import { Navigation } from "@/components/sections/Navigation";
import { Footer } from "@/components/sections/Footer";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  return (
    <div className={`min-h-screen ${isRTL ? "rtl" : "ltr"}`}>
      <Navigation isRTL={isRTL} setRegistrationType={() => {}} handleRegistrationClick={() => {}} />
      
      <div className="container max-w-4xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-primary hover:underline mb-8">
          <ArrowRight className={`w-4 h-4 ${isRTL ? "" : "rotate-180"}`} />
          {isRTL ? "العودة للرئيسية" : "Back to Home"}
        </Link>

        <h1 className="text-3xl font-bold text-foreground mb-8">
          {isRTL ? "سياسة الخصوصية" : "Privacy Policy"}
        </h1>

        <div className="prose prose-lg max-w-none space-y-6 text-muted-foreground">
          {isRTL ? (
            <>
              <section>
                <h2 className="text-xl font-semibold text-foreground">مقدمة</h2>
                <p>نحن في "دليل يدل - Yadel" نلتزم بحماية خصوصية مستخدمينا. توضح سياسة الخصوصية هذه كيفية جمع معلوماتك الشخصية واستخدامها وحمايتها عند استخدامك لمنصتنا.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground">المعلومات التي نجمعها</h2>
                <ul className="list-disc pr-6 space-y-2">
                  <li>معلومات الحساب: الاسم، البريد الإلكتروني، رقم الهاتف</li>
                  <li>معلومات الاستخدام: الصفحات التي تزورها، الفعاليات التي تتفاعل معها</li>
                  <li>معلومات الجهاز: نوع المتصفح، عنوان IP، نظام التشغيل</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground">كيف نستخدم معلوماتك</h2>
                <ul className="list-disc pr-6 space-y-2">
                  <li>تقديم خدمات المنصة وتحسينها</li>
                  <li>إرسال إشعارات حول الفعاليات التي تهمك</li>
                  <li>تحسين تجربة المستخدم وتخصيص المحتوى</li>
                  <li>التواصل معك بخصوص حسابك أو خدماتنا</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground">حماية المعلومات</h2>
                <p>نتخذ إجراءات أمنية مناسبة لحماية معلوماتك الشخصية من الوصول غير المصرح به أو التغيير أو الإفصاح أو الإتلاف.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground">مشاركة المعلومات</h2>
                <p>لا نبيع أو نؤجر معلوماتك الشخصية لأطراف ثالثة. قد نشارك معلوماتك فقط في الحالات التالية:</p>
                <ul className="list-disc pr-6 space-y-2">
                  <li>بموافقتك الصريحة</li>
                  <li>للامتثال للمتطلبات القانونية</li>
                  <li>لحماية حقوقنا أو سلامة مستخدمينا</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground">حقوقك</h2>
                <p>يحق لك طلب الوصول إلى بياناتك الشخصية أو تصحيحها أو حذفها في أي وقت عن طريق التواصل معنا.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground">التواصل</h2>
                <p>لأي استفسارات حول سياسة الخصوصية، يرجى التواصل معنا عبر البريد الإلكتروني أو من خلال المنصة.</p>
              </section>

              <p className="text-sm text-muted-foreground mt-8">آخر تحديث: مارس 2026</p>
            </>
          ) : (
            <>
              <section>
                <h2 className="text-xl font-semibold text-foreground">Introduction</h2>
                <p>At "Yadel", we are committed to protecting the privacy of our users. This Privacy Policy explains how we collect, use, and protect your personal information when you use our platform.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground">Information We Collect</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Account information: name, email, phone number</li>
                  <li>Usage information: pages visited, events interacted with</li>
                  <li>Device information: browser type, IP address, operating system</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground">How We Use Your Information</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Providing and improving platform services</li>
                  <li>Sending notifications about events that interest you</li>
                  <li>Improving user experience and personalizing content</li>
                  <li>Communicating with you about your account or our services</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground">Information Protection</h2>
                <p>We take appropriate security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground">Information Sharing</h2>
                <p>We do not sell or rent your personal information to third parties. We may share your information only in the following cases:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>With your explicit consent</li>
                  <li>To comply with legal requirements</li>
                  <li>To protect our rights or the safety of our users</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground">Your Rights</h2>
                <p>You have the right to request access to, correction, or deletion of your personal data at any time by contacting us.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground">Contact</h2>
                <p>For any inquiries about our Privacy Policy, please contact us via email or through the platform.</p>
              </section>

              <p className="text-sm text-muted-foreground mt-8">Last updated: March 2026</p>
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
